import EventBus from '@mvps-genesis/event-bus';
import {ParameterValidator} from '@mvps-genesis/utilities';

export default class MiniBasket {

	// Define "private" properties
	_validator;

	// Define "public" properties
	isOpen = false;

	// eslint-disable-next-line max-lines-per-function
	constructor ({
		containerElement = '.js-mini-basket',
		containerElementMobile = '.js-mini-basket-mobile',
		closeElements = '.js-mini-basket-close',
		openElements = '.js-mini-basket-open',
		countElements = '.js-mini-basket-count',
		subtotalElements = '.js-mini-basket-subtotal',
		removeElements = '.js-mini-basket-remove',
		openEvent = 'click',
		closeEvent = 'click',
		isOpenByDefault = false,
		closeOnEsc = true,
		openOnAddToCart = false,
		closeAfterOpen = false,
		closeAfterOpenTime = 5000
	} = {}) {

		// Create validator
		this._validator = new ParameterValidator(this);

		// Save selectors
		this.elementSelectors = {
			closeElements,
			containerElement,
			containerElementMobile,
			countElements,
			openElements,
			removeElements,
			subtotalElements
		};

		// Create properties from options
		this.containerElement = this._validator.isElement('options.containerElement', containerElement);
		this.containerElementMobile = this._validator.isElement('options.containerElementMobile', containerElementMobile);
		this.closeElements = this._validator.isElementList('options.closeElements', closeElements);
		this.openElements = this._validator.isElementList('options.openElements', openElements);
		this.countElements = this._validator.isElementList('options.countElements', countElements);
		this.subtotalElements = this._validator.isElementList('options.subtotalElements', subtotalElements);
		this.removeElements = this._validator.isElementList('options.removeElements', removeElements);
		this.openEvent = this._validator.isString('options.openEvent', openEvent);
		this.closeEvent = this._validator.isString('options.closeEvent', closeEvent);
		this.isOpenByDefault = this._validator.isBoolean('options.isOpenByDefault', isOpenByDefault);
		this.closeOnEsc = this._validator.isBoolean('options.closeOnEsc', closeOnEsc);
		this.openOnAddToCart = this._validator.isBoolean('options.openOnAddToCart', openOnAddToCart);
		this.closeAfterOpen = this._validator.isBoolean('options.closeAfterOpen', closeAfterOpen);
		this.closeAfterOpenTime = this._validator.isInt('options.closeAfterOpenTime', closeAfterOpenTime);
		this.isIOS = (/iPad|iPhone|iPod/).test(navigator.platform) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

		// Run bind events init function
		this.bindEvents();

		// Run open command if isOpenByDefault is true
		if (this.isOpenByDefault) {
			this.open();
		}

		this.autoOpenTimeout = '';
		this.autoCloseTimeout = '';

	}

	bindEvents () {

		// Listen for global event - `MVTPS:addToCart` and handle response
		EventBus.addEventListener('MVPS:addToCart', this.onAddToCart.bind(this), false);

		// Listen for global event - `WarrantyCheckout:clearCart` and handle response
		EventBus.addEventListener('WarrantyCheckout:clearCart', this.onAddToCart.bind(this), false);

		// Bind events for buttons
		for (let element of this.openElements) {

			element.addEventListener(this.openEvent, this.toggle.bind(this), false);

		}

		for (let element of this.closeElements) {

			element.addEventListener(this.closeEvent, this.close.bind(this), false);

		}

		for (let element of this.removeElements) {

			element.addEventListener('click', () => this.removeItem(element), false);

		}

		document.addEventListener('click', (e) => this.documentClick(e), false);

	}

	documentClick (event) {
		if (!event.target.closest('.x-mini-basket') &&
			!event.target.closest(this.elementSelectors.closeElements) &&
			!event.target.closest(this.elementSelectors.openElements)
		) {
			this.close();
		}
	}

	onKeyup (e) {

		if (e.keyCode === 27) {

			this.close();

		}

	}

	onAddToCart (e) {
		const payload = e.detail;

		if (payload.success) {

			this.onAddToCartSuccess(payload);

		} else {

			this.onAddToCartError(payload);

		}

	};

	// eslint-disable-next-line max-lines-per-function
	onAddToCartSuccess (payload) {
		const self = this;

		if (this.autoOpenTimeout) {
			clearTimeout(this.autoOpenTimeout);
		}

		if (this.autoCloseTimeout) {
			clearTimeout(this.autoCloseTimeout);
		}

		let leaveOpen = 0;
		// Replace container element with payload version
		this.replaceContainerElement(this.getContainerElementFromPayload(payload));
		this.replaceContainerElementMobile(this.getContainerElementFromPayloadMobile(payload));

		// Re-bind close elements
		this.closeElements = this._validator.isElementList('options.closeElements', this.elementSelectors.closeElements);
		for (let element of this.closeElements) {
			element.addEventListener(this.closeEvent, this.close.bind(this), false);
		}

		this.removeElements = this._validator.isElementList('options.removeElements', this.elementSelectors.removeElements);
		for (let element of this.removeElements) {
			element.addEventListener('click', () => this.removeItem(element), false);
		}

		// Set count and subtotal
		this.count = this.getCount();
		this.subtotal = this.getSubtotal();

		// Update count / subtotal elements
		this.updateCounts();
		this.updateSubtotals();

		// If this was already opened, leave it open so it doesn't jump around
		if (this.isOpen) {
			this.open();
			// If no items in cart, don't leaveOpen
			if (this.count > 0) {
				leaveOpen = 1;
			}
		}

		// Open mini basket if configured
		if (this.openOnAddToCart && leaveOpen === 0 && !payload.doNotOpen) {

			this.autoOpenTimeout = setTimeout(() => {

				if (window.innerWidth >= 960) {
					this.open();
				} else {
					document.querySelector(self.elementSelectors.containerElementMobile).classList.add('x-mini-basket--open');
					setTimeout(function () {
						document.querySelector(self.elementSelectors.containerElementMobile).classList.remove('x-mini-basket--open');
					}, 3000);
				}

				// Close mini basket after X ms if configured
				if (this.closeAfterOpen) {
					this.autoCloseTimeout = setTimeout(this.close.bind(this), this.closeAfterOpenTime);
				}

			}, 200);

		}

	}

	onAddToCartError (payload) {

		return false;

	}

	getContainerElementFromPayload (payload) {

		return payload.data.querySelector(this.elementSelectors.containerElement);

	}

	replaceContainerElement (newContainer) {

		this.containerElement.parentNode.replaceChild(newContainer, this.containerElement);
		this.containerElement = newContainer;

	}

	getContainerElementFromPayloadMobile (payload) {

		return payload.data.querySelector(this.elementSelectors.containerElementMobile);

	}

	replaceContainerElementMobile (newContainer) {
		let newClone = newContainer.cloneNode(true);

		this.containerElement.parentNode.replaceChild(newClone, this.containerElementMobile);
		this.containerElementMobile = newClone;

	}

	getCount () {

		return this.containerElement.dataset.count;


	}

	getSubtotal () {

		return this.containerElement.dataset.subtotal;

	}

	updateCounts () {

		for (let element of this.countElements) {

			element.textContent = this.count;

		}

		window.mivaJS.Basket_Count = parseFloat(this.count);

	}

	updateSubtotals () {

		for (let element of this.subtotalElements) {

			element.textContent = this.subtotal;

		}

	}

	open (e) {

		if (e) {
			e.preventDefault();
		}

		this.isOpen = true;
		this.containerElement.classList.add('x-mini-basket--open');
		document.body.classList.add('x-mini-basket--is-open');

		if (this.isIOS) {
			const scrollY = window.scrollY;
			const negativeScrollY = scrollY * -1;
			document.documentElement.style.top = negativeScrollY + 'px';
			document.documentElement.setAttribute('data-scroll-distance', scrollY);
			document.documentElement.classList.add('x-mini-basket--is-open-ios');
		}

		// Bind event for ESC button close
		if (this.closeOnEsc) {
			document.addEventListener('keyup', this.onKeyup.bind(this), false);
		}

	}

	close (e) {

		if (e) {
			e.preventDefault();
		}

		this.isOpen = false;
		this.containerElement.classList.remove('x-mini-basket--open');
		document.body.classList.remove('x-mini-basket--is-open');

		if (this.isIOS && document.documentElement.classList.contains('x-mini-basket--is-open-ios')) {
			document.documentElement.classList.remove('x-mini-basket--is-open-ios');
			document.documentElement.style.top = null;
			window.scrollTo(0, parseFloat(document.documentElement.getAttribute('data-scroll-distance')));
			document.documentElement.removeAttribute('data-scroll-distance');
		}


		// Remove event for ESC button close
		if (this.closeOnEsc) {
			document.removeEventListener('keyup', this.onKeyup.bind(this), false);
		}

	}

	toggle (e) {

		if (e) {
			e.preventDefault();
			e.stopImmediatePropagation();
		}

		return this.isOpen ? this.close() : this.open();
	}

	async removeItem (elem) {
		elem.disabled = true;

		const url = elem.getAttribute('data-href');
		const textElement = elem.querySelector('.js-mini-basket-remove-text');

		if (textElement) {
			textElement.textContent = 'Removing...';
		}

		const response = await this.removeItemRequest(url);
		const html = document.createElement('div');
		html.innerHTML = response;

		this.onAddToCartSuccess({
			data: html
		});
	}

	async removeItemRequest (url) {

		const response = await fetch(url, {
			method: 'POST'
		});

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.text();
	}

};
